import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { WherebyProvider } from '@whereby.com/browser-sdk/react'
import { WherebyCallOwnershipStatus } from '../../../../../../../pure-js/enums/Whereby'
import { shouldAskBeforeConnectingWhereby } from '../../../../../../../pure-js/libs/wherebyCallOwnership'
import { WherebyCallSurface } from '../../../components/whereby/WherebyCallSurface'
import { WherebyCallBlockedPanel } from '../../../components/whereby/WherebyCallBlockedPanel'
import { useWherebyConnectionKey } from '../../../components/whereby/wherebyCallReconnect'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { AI_COACH_STYLES } from '../constants'

interface AICoachVideoPanelProps {
  /** The debrief room is ready AND this tab holds the user's single allowed call. */
  canJoinTheCall: boolean
  roomUrl?: string
  userId?: string
  displayName: string
  teamName: string
  hasUserLeftCall: boolean
  ownershipStatus: WherebyCallOwnershipStatus
  onAssistantPresenceChange: (isPresent: boolean) => void
  onLeaveCall: () => void
  onConfirmTakeover: () => void
  onRetryOwnership: () => void
}

// Shows exactly one debrief state: the call, its ownership prompt, or room preparation.
export const AICoachVideoPanel: React.FC<AICoachVideoPanelProps> = ({
  canJoinTheCall,
  roomUrl,
  userId,
  displayName,
  teamName,
  hasUserLeftCall,
  ownershipStatus,
  onAssistantPresenceChange,
  onLeaveCall,
  onConfirmTakeover,
  onRetryOwnership
}) => {
  const { t: translate } = useTranslation()

  const shouldShowCall = canJoinTheCall && Boolean(roomUrl)
  const shouldShowOwnershipPrompt = !shouldShowCall && shouldAskBeforeConnectingWhereby(ownershipStatus)
  const shouldShowWaiting = !shouldShowCall && !shouldShowOwnershipPrompt
  const callRoomUrl = shouldShowCall ? roomUrl : undefined
  const { connectionKey, startFreshConnection } = useWherebyConnectionKey(callRoomUrl)
  const waitingContent = hasUserLeftCall ? (
    <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: { xs: '13px', sm: '14px', md: '14px' } }}>
      {translate('aiCoach.ui.youLeftTheCall')}
    </Typography>
  ) : (
    <>
      <CircularProgress size={24} sx={{ color: INTERAX_COLORS.sun }} />
      <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: { xs: '13px', sm: '14px', md: '14px' } }}>
        {translate('aiCoach.ui.connectingToVideo')}
      </Typography>
    </>
  )

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: '100%', md: '60%' },
        minHeight: { xs: '280px', sm: '320px', md: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: { xs: 'none', sm: 'none', md: 1 }
      }}
    >
      {callRoomUrl ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <WherebyProvider key={connectionKey}>
            <WherebyCallSurface
              roomUrl={callRoomUrl}
              userId={userId}
              displayName={displayName}
              teamName={teamName}
              onAssistantPresenceChange={onAssistantPresenceChange}
              onReconnect={startFreshConnection}
              onLeaveCall={onLeaveCall}
            />
          </WherebyProvider>
        </Box>
      ) : null}

      {shouldShowOwnershipPrompt ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <WherebyCallBlockedPanel
            status={ownershipStatus}
            onConfirmTakeover={onConfirmTakeover}
            onRetry={onRetryOwnership}
          />
        </Box>
      ) : null}

      {shouldShowWaiting ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: INTERAX_COLORS.black30,
            borderRadius: AI_COACH_STYLES.borderRadius.medium,
            gap: 1
          }}
        >
          {waitingContent}
        </Box>
      ) : null}
    </Box>
  )
}
